import { AnnotateHaug } from '../../../@resources/help';
import { idGenerator, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { Camera } from '../../canvas/Camera';
import { defineToolWithCommonBehaviors } from '../../canvas/tooling/Definitions';
import { ToolBehavior } from '../../canvas/tooling/ToolBehavior';
import { resolve } from '../../Container';
import { HelpController } from '../../core/help/HelpController';
import { HelpTip } from '../../core/help/HelpTip';
import { ProfileModel } from '../../ProfileModel';
import { Annotation } from './Annotation';
import { GesturesController } from './GesturesController';

const annotateHaugTip = HelpTip(
    'annotateHaug',
    'videos',
    'The Vanishing Pen',
    'We\'ve recommended a tutorial video for the Vanishing Pen.'
);

const nextId = idGenerator(uuid('nodash').slice(0, 8));

class AnnotateToolBehavior extends ToolBehavior {

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(GesturesController) private ctrl!: GesturesController;
    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(HelpController) private help!: HelpController;

    private data: number[] = [];

    private latestAt: number = -99999;
    private latestId = null as string;
    private latestGroup = nextId();
    private latestPt = null as Point | null;

    public getPreferredCursor(_state: string, _xi: MouseInput | KeyInput) {
        return 'draw';
    }

    public init() {
        if (this.help.recommendVideo(AnnotateHaug)) {
            this.help.openTip(annotateHaugTip);
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (state !== 'idle' || mi.button !== MouseButtons.Main) {
            return;
        }

        // Each annotation gets a new id, but we change group after a gap in input that exceeds the
        // Annotation.ttl (plus a bit of margin for // error)
        this.latestId = nextId();
        const now = performance.now();
        if ((now - this.latestAt) > (Annotation.ttl * 1.1)) {
            this.latestGroup = nextId();
        }

        this.latestAt = now;
        this.latestPt = this.camera.toModelPoint(mi.position);
        this.data = [this.latestPt.x, this.latestPt.y];

        return 'annotating';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        if (state !== 'annotating') {
            return;
        }

        const now = performance.now();
        const d = now - this.latestAt;
        if (d >= 15) {
            this.append(this.camera.toModelPoint(mi.position), mi.modifiers.shift);
            this.latestAt = now;
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state !== 'annotating') {
            return;
        }

        this.append(this.camera.toModelPoint(mi.position), mi.modifiers.shift);

        this.data = null;
        this.latestPt = null;
        this.latestId = null;
        return 'idle';
    }

    private append(next: Point, _correct: boolean) {
        this.data.push(next.x, next.y);
        this.latestPt = next;
        this.ctrl.annotate(this.latestId, this.latestGroup, this.me.color, this.data);
    }
}

export const annotateTool = defineToolWithCommonBehaviors({
    id: 'annotateTool',
    label: 'Vanishing Pen',
    category: 'gestures',
    icon: 'deqo://icons/hero/pencil-alt',
    weight: 4020,
    shortcut: '2',
    behaviors: [
        AnnotateToolBehavior,
    ]
});
